import React, { useMemo, useState } from 'react';
import { CircleDollarSignIcon, Clock3Icon, InfoIcon, LockKeyholeIcon, Settings2Icon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { GeographySelector } from './GeographySelector';
type Channel = {
  id: string;
  name: string;
  iconUrl?: string;
};
const channels: Channel[] = [{
  id: 'manymoons',
  name: 'Manymoons',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'
}, {
  id: 'depop',
  name: 'Depop',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fG9XmMcsDQikNiC1uba9MJ/dopop.svg'
}, {
  id: 'ebay',
  name: 'eBay',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/7ctVShw3HBQcunanqiLN75/ebay.svg'
}, {
  id: 'mercari',
  name: 'Mercari',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/cisCD3tYpjAhP5v7GoDZxD/mercari.svg'
}, {
  id: 'poshmark',
  name: 'Poshmark',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg'
}, {
  id: 'prive',
  name: 'Privé',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg'
}, {
  id: 'tiktok',
  name: 'TikTok Live',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/9zCU7ZyrDgBa4HCXG66eUn/tiktok.svg'
}, {
  id: 'vestiaire',
  name: 'Vestiaire',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/tLawC51vDdyw6q2RLyZ5Bc/vestiaire.svg'
}, {
  id: 'vinted',
  name: 'Vinted',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/6n8bfmq2ixHXbjbeTaLCJz/vinted.svg'
}, {
  id: 'whatnot',
  name: 'Whatnot',
  iconUrl: 'https://cdn.magicpatterns.com/uploads/tKhY3ZxiXEuMX34dV7VA57/whatnot.svg'
}];
function recoveryCents(months: number) {
  if (months <= 2) return 10;
  if (months <= 6) return 10 + (months - 2) * 30 / 4;
  return 40 + (months - 6) * 20 / 6;
}
export function ControlsDashboard() {
  const [activeChannels, setActiveChannels] = useState<string[]>(['manymoons', 'ebay', 'whatnot']);
  const [infoChannel, setInfoChannel] = useState<Channel | null>(null);
  const [indexPrice, setIndexPrice] = useState(50);
  const [indexAgreement, setIndexAgreement] = useState(50);
  const [indexLocked, setIndexLocked] = useState(false);
  const [floorPrice, setFloorPrice] = useState(10);
  const [floorAgreement, setFloorAgreement] = useState(10);
  const [floorLocked, setFloorLocked] = useState(false);
  const [timeHorizon, setTimeHorizon] = useState(6);
  const projectedCents = useMemo(() => Math.round(recoveryCents(timeHorizon)), [timeHorizon]);
  const toggleChannel = (channelId: string) => {
    setActiveChannels(selected => selected.includes(channelId) ? selected.filter(id => id !== channelId) : [...selected, channelId]);
  };
  const updateIndexAgreement = (value: number) => {
    const nextValue = Math.max(0, Math.min(100, value));
    setIndexAgreement(nextValue);
    if (indexLocked) setIndexPrice(current => Math.min(current, nextValue));
  };
  const updateFloorAgreement = (value: number) => {
    const nextValue = Math.max(4, Math.min(100, value));
    setFloorAgreement(nextValue);
    if (floorLocked) setFloorPrice(current => Math.min(current, nextValue));
  };
  const setIndexAgreementLock = (locked: boolean) => {
    setIndexLocked(locked);
    if (locked) setIndexPrice(current => Math.min(current, indexAgreement));
  };
  const setFloorAgreementLock = (locked: boolean) => {
    setFloorLocked(locked);
    if (locked) setFloorPrice(current => Math.min(current, floorAgreement));
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="controls-heading" data-id="element-302" data-mp="170cn9f3aqsip">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-303" data-mp="1brcjsj8i78ko">
        <div data-id="element-304" data-mp="y0fup2dsd9id">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-305" data-mp="y0pnsyu4nwsa">
            <Settings2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-306" data-mp="11yx2lo516o0u" />
            Controls
          </span>
          <h1 id="controls-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-307" data-mp="o3kwkklfdb4m">
            Set your recovery rules.
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-308" data-mp="1vd7ul2y626wo">
            Choose where inventory can be sold and the price guardrails Carli should use when building your recovery plan.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-309" data-mp="1hmciziwa9bpe">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-310" data-mp="1cnhz0dmkxh3p" />
          Changes save automatically
        </span>
      </section>

      <section className="mt-8 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="channels-heading" data-id="element-311" data-mp="17mtzf3kabrxv">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-312" data-mp="yngqqd3zj7e7">
          <div data-id="element-313" data-mp="yptcz2gs03x2">
            <div className="flex items-center gap-2" data-id="element-314" data-mp="2y0b14kv9hbbt">
              <SlidersHorizontalIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-315" data-mp="2xzt8dy7t5f8n" />
              <h2 id="channels-heading" className="text-lg font-semibold" data-id="element-316" data-mp="2t215681pq6p5">Approved channels</h2>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-ink/60" data-id="element-317" data-mp="30iwmm1au59av">Select the marketplaces where this inventory is eligible to appear.</p>
          </div>
          <span className="text-sm font-medium text-ink/55" data-id="element-318" data-mp="3aak87lkpnw4f">{activeChannels.length} selected</span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-319" data-mp="3f8lpq22hnnds">
          {channels.map(channel => {
          const selected = activeChannels.includes(channel.id);
          const infoOpen = infoChannel?.id === channel.id;
          return <div key={channel.id} className="relative" data-id="element-320" data-mp="2rkkn19rzr16a">
                <button type="button" onClick={() => toggleChannel(channel.id)} aria-pressed={selected} className={`flex min-h-[76px] w-full items-center gap-2.5 rounded-xl border p-3 pr-7 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'border-[#BB5E40] bg-[#BB5E40] text-cream shadow-sm' : 'border-ink/10 bg-cream/40 hover:border-[#BB5E40]/30'}`} data-id="element-321" data-mp="2rj14fciv4e1c">
                  <ChannelBadge channel={channel} selected={selected} data-id="element-322" data-mp="2gx10ldj0d8d8" />
                  <span className={`min-w-0 flex-1 whitespace-normal break-words text-[13px] font-semibold leading-tight sm:text-sm ${selected ? 'text-cream' : 'text-ink'}`} data-id="element-323" data-mp="2gw818xmdpl54">{channel.name}</span>
                </button>
                <button type="button" onClick={() => setInfoChannel(infoOpen ? null : channel)} aria-label={`About ${channel.name}`} aria-expanded={infoOpen} className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20 ${selected ? 'text-cream/75 hover:bg-cream/15 hover:text-cream' : 'text-ink/45 hover:bg-ink/5 hover:text-[#BB5E40]'}`} data-id="element-324" data-mp="1o5zfkvsmwils">
                  <InfoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-325" data-mp="3c409ygvyima4" />
                </button>
                {infoOpen && <div role="dialog" aria-label={`About ${channel.name}`} className="absolute left-0 top-[calc(100%+8px)] z-20 w-64 rounded-xl border border-ink/10 bg-paper p-4 text-left shadow-lg" data-id="element-326" data-mp="335omgnqe04lw">
                    <div className="flex items-start justify-between gap-3" data-id="element-327" data-mp="3vudhc50undp7">
                      <div data-id="element-328" data-mp="3vs9elqeoskqi">
                        <p className="text-sm font-semibold text-ink" data-id="element-329" data-mp="3qtacsaep2ny">{channel.name}</p>
                        <p className="mt-1 text-xs leading-relaxed text-ink/60" data-id="element-330" data-mp="3lw69qcsga7zv">Channel description coming soon. Add the approved positioning, audience, and merchandising notes for this marketplace here.</p>
                      </div>
                      <button type="button" onClick={() => setInfoChannel(null)} className="-mr-1 -mt-1 rounded-md p-1 text-ink/45 transition-colors hover:bg-ink/5 hover:text-ink" aria-label={`Close ${channel.name} details`} data-id="element-331" data-mp="2afzj43q7fsh4">
                        <XIcon className="h-3.5 w-3.5" data-id="element-332" data-mp="326oomqhxc95r" />
                      </button>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" data-id="element-333" data-mp="3pug3o1uun0aq">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="pricing-heading" data-id="element-334" data-mp="3pvr9s8brok50">
          <div className="flex items-center gap-2" data-id="element-335" data-mp="3g3tv2rljva1j">
            <CircleDollarSignIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-336" data-mp="3g2ad8wk8wqxn" />
            <h2 id="pricing-heading" className="text-lg font-semibold" data-id="element-337" data-mp="3b4h7g4mutyxw">Pricing guardrails</h2>
          </div>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-338" data-mp="cgbbr7ukr69c">These settings guide public merchandising and the lowest acceptable recovery price.</p>

          <div className="mt-7 space-y-7" data-id="element-339" data-mp="fxpoi9w3hfqy">
            <PricingSlider label="Index price" value={indexPrice} onChange={setIndexPrice} helper="The public price displayed online—on Manymoons, marketplaces, and search listings—before any customer discount codes are applied." min={0} max={indexLocked ? indexAgreement : 100} agreement={indexAgreement} onAgreementChange={updateIndexAgreement} locked={indexLocked} onLockChange={setIndexAgreementLock} data-id="element-340" data-mp="fz0qq59w6ffy" />
            <PricingSlider label="Floor price" value={floorPrice} onChange={setFloorPrice} helper="The lowest price you are willing to accept for a unit, expressed as a percentage of its MSRP. This setting is independent of your public index price." min={4} max={floorLocked ? floorAgreement : 100} agreement={floorAgreement} onAgreementChange={updateFloorAgreement} locked={floorLocked} onLockChange={setFloorAgreementLock} data-id="element-341" data-mp="17p55noevua9s" />
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream sm:p-7" aria-labelledby="horizon-heading" data-id="element-342" data-mp="doe4dqwdl8xo">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-343" data-mp="ihz5hrt9mj13">
            <Clock3Icon className="h-4 w-4" aria-hidden="true" data-id="element-344" data-mp="ne7fyrwipo1x" />
            <h2 id="horizon-heading" className="text-lg font-semibold text-cream" data-id="element-345" data-mp="sdkck4d6y9hp">Sales horizon</h2>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-cream/70" data-id="element-346" data-mp="wffd36nasiuf">More time lets Carli preserve value with less aggressive pricing.</p>

          <div className="mt-7 rounded-2xl border border-cream/15 bg-cream/[0.08] p-4" data-id="element-347" data-mp="11dyyn5xzbsm9">
            <div className="flex items-end justify-between gap-3" data-id="element-348" data-mp="2y8ldqfycbqjp">
              <span className="text-sm font-medium text-cream/75" data-id="element-349" data-mp="335bs8yuhye30">Time to sell</span>
              <span className="font-display text-3xl font-semibold tracking-tight" data-id="element-350" data-mp="2vn45868gioaz">{timeHorizon} months</span>
            </div>
            <input type="range" min={2} max={12} step={1} value={timeHorizon} onChange={event => setTimeHorizon(Number(event.target.value))} style={{
            '--horizon-progress': `${(timeHorizon - 2) / 10 * 100}%`
          } as React.CSSProperties} className="controls-horizon-slider mt-5 w-full" aria-label="Sales horizon in months" data-id="element-351" data-mp="2quuggtj2vuaa" />
            <div className="mt-1 flex justify-between text-xs text-cream/55" data-id="element-352" data-mp="34n0nnq496qxl">
              <span data-id="element-353" data-mp="34p51p0vv96bl">2 months</span>
              <span data-id="element-354" data-mp="30m8eewvdsm1b">12 months</span>
            </div>
          </div>

          <div className="mt-5 border-t border-cream/15 pt-5" data-id="element-355" data-mp="30o1j46mfow6o">
            <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-356" data-mp="1t9ose3mk7gn1">Projected recovery</p>
            <p className="mt-2 font-display text-4xl font-semibold tracking-tight" data-id="element-357" data-mp="1p58lbz17m368">{projectedCents}¢ <span className="text-lg font-medium text-cream/65" data-id="element-358" data-mp="1qo8n8xvsq1vm">per $1 MSRP</span></p>
            <p className="mt-2 text-sm leading-relaxed text-cream/70" data-id="element-359" data-mp="1vlqumju6091i">At this horizon, Carli will aim to recover approximately {projectedCents} cents for every MSRP dollar while maintaining your pricing guardrails.</p>
          </div>
        </article>
      </section>

      <GeographySelector data-id="element-360" data-mp="17xhgm4370pkx" />
    </div>;
}
function ChannelBadge({
  channel,
  selected
}: {
  channel: Channel;
  selected: boolean;
}) {
  return <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 ${selected ? 'bg-cream/15' : 'bg-[#BB5E40]'}`} data-id="element-361" data-mp="1copsd6rzwn19"><img src={channel.iconUrl} alt="" className={`h-full w-full object-contain ${channel.id === 'ebay' ? 'scale-125' : ''}`} data-id="element-362" data-mp="xwxd0us7b8zj" /></span>;
}
function PricingSlider({
  label,
  value,
  onChange,
  helper,
  min,
  max,
  agreement,
  onAgreementChange,
  locked,
  onLockChange
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  helper: string;
  min: number;
  max: number;
  agreement: number;
  onAgreementChange: (value: number) => void;
  locked: boolean;
  onLockChange: (locked: boolean) => void;
}) {
  const agreementId = `${label.toLowerCase().replace(' ', '-')}-agreement`;
  const lockId = `${label.toLowerCase().replace(' ', '-')}-lock`;
  return <div data-id="element-363" data-mp="eboqsqbj7fzq">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-364" data-mp="ebx58mnaytd1">
        <label className="pt-1 text-sm font-semibold text-ink" data-id="element-365" data-mp="t8xy4th8cud3">{label}</label>
        <div className="flex items-center gap-2 self-start rounded-xl border border-ink/10 bg-cream/65 p-1.5" data-id="element-366" data-mp="oacxwu1zaaon">
          <label htmlFor={agreementId} className="sr-only" data-id="element-367" data-mp="121j4tb7l7alk">Agreed {label.toLowerCase()} ceiling</label>
          <input id={agreementId} type="number" min={min} max={100} value={agreement} onChange={event => onAgreementChange(Number(event.target.value))} className="w-12 bg-transparent text-right text-sm font-semibold tabular-nums text-ink focus:outline-none" data-id="element-368" data-mp="y1r2vhcp82p6" />
          <span className="text-xs font-medium text-ink/50" data-id="element-369" data-mp="1v5dcafvbpqcp">% agreed</span>
          <span className="h-5 w-px bg-ink/10" aria-hidden="true" data-id="element-370" data-mp="1v392xiennz51" />
          <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-4 w-4 rounded border-ink/25 accent-[#BB5E40]" data-id="element-371" data-mp="1zwvet8czvyzs" />
          <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-372" data-mp="1c6hkfb6xto21">
            <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-373" data-mp="1h5kootf3fq8y" />
            Lock
          </label>
        </div>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-ink/55" data-id="element-374" data-mp="2sl9c1lw9eraa">
        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-375" data-mp="2xju4v096m8op" />
        <p data-id="element-376" data-mp="lwqvyg6iusa3">{helper}</p>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3" data-id="element-377" data-mp="lvp9lfk22jd2">
        <span className="text-xs font-medium uppercase tracking-wider text-ink/45" data-id="element-378" data-mp="gxxksuepjdd3">Current setting</span>
        <span className="font-display text-2xl font-semibold tabular-nums text-[#BB5E40]" data-id="element-379" data-mp="c6fhw0t4j2ph">{value}% <span className="font-sans text-xs font-medium text-ink/45" data-id="element-380" data-mp="7847noyx2q6q">of MSRP</span></span>
      </div>
      <input type="range" min={min} max={max} step={1} value={value} onChange={event => onChange(Number(event.target.value))} className="mt-3 w-full accent-[#BB5E40]" aria-label={`${label} percentage of MSRP`} data-id="element-381" data-mp="35zhj07ogbou" />
      <div className="mt-1 flex justify-between text-xs text-ink/45" data-id="element-382" data-mp="3pfaf8t782nsr">
        <span data-id="element-383" data-mp="3kfxmjrto6mi9">{min}%</span>
        <span data-id="element-384" data-mp="3kgqu2v0gdg1g">{max}%{locked ? ' agreement ceiling' : ''}</span>
      </div>
    </div>;
}